import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link, useSearchParams } from "react-router-dom";
import { RouteConstants } from "../../config/constants/route";
import { useQuery } from "react-query";
import { request } from "../../config/helpers/axios-instance";
import Loader from "../../components/Loader";
import SourcesTable from "../../components/cart-grounding/SourcesTable";
import FunnelStrip from "../../components/cart-grounding/FunnelStrip";
import CartList from "../../components/cart-grounding/CartList";
import IdentityComparison from "../../components/cart-grounding/IdentityComparison";
import CartJourney from "../../components/cart-grounding/CartJourney";
import FunnelSummary from "../../components/cart-grounding/FunnelSummary";
import { MdInsights, MdKeyboardArrowDown, MdKeyboardArrowRight } from "react-icons/md";

const DECISION_COLORS = {
    EXACT:     "bg-green-100 text-green-700",
    FUZZY:     "bg-blue-100 text-blue-700",
    SUGGEST:   "bg-yellow-100 text-yellow-700",
    UNMATCHED: "bg-red-100 text-red-700",
    SKIPPED:   "bg-gray-100 text-gray-500",
};

const PRICING_COLORS = {
    READY:  "bg-green-100 text-green-700",
    FAILED: "bg-red-100 text-red-700",
    REVIEW: "bg-yellow-100 text-yellow-700",
};

function Badge({ label, colorClass }) {
    return (
        <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-semibold font-sans ${colorClass || "bg-gray-100 text-gray-500"}`}>
            {label || "—"}
        </span>
    );
}

Badge.propTypes = {
    label:      PropTypes.string,
    colorClass: PropTypes.string,
};

Badge.defaultProps = { label: "—", colorClass: "" };

function MedicineTableRow({ line }) {
    const [expanded, setExpanded] = useState(false);
    const { grounding, pricing } = line;
    const sourcesCount  = pricing?.sources?.length ?? 0;
    const matchedCount  = (pricing?.sources || []).filter((s) => s.gateDecision === "MATCH").length;
    const winner        = (pricing?.sources || []).find((s) => s.selected);

    return (
        <>
            <tr
                className="border-t border-gray-100 h-12 hover:bg-gray-50 cursor-pointer"
                onClick={() => setExpanded((v) => !v)}
            >
                {/* Medicine */}
                <td className="pl-3 py-3">
                    <div className="font-medium text-gray-800">{line.itemName}</div>
                    {(line.drugForm || line.doseUnit) && (
                        <div className="text-xs text-gray-400 mt-0.5">
                            {[line.drugForm, line.doseUnit].filter(Boolean).join(" · ")}
                        </div>
                    )}
                </td>

                {/* Grounding decision */}
                <td className="pl-3 py-3">
                    <Badge
                        label={grounding?.decision}
                        colorClass={DECISION_COLORS[grounding?.decision]}
                    />
                    {grounding?.matchBasis && (
                        <div className="text-[10px] text-gray-400 mt-0.5">via {grounding.matchBasis}</div>
                    )}
                </td>

                {/* Matched medicine ID */}
                <td className="pl-3 py-3 text-xs text-gray-500 max-w-[160px] truncate">
                    {grounding?.matchedMedicineId || (
                        grounding?.rejectionReason
                            ? <span className="text-red-400">{grounding.rejectionReason}</span>
                            : "—"
                    )}
                    {grounding?.candidateCount != null && (
                        <div className="text-[10px] text-gray-400">{grounding.candidateCount} candidates</div>
                    )}
                </td>

                {/* Pricing status */}
                <td className="pl-3 py-3">
                    <Badge
                        label={pricing?.linePricingStatus}
                        colorClass={PRICING_COLORS[pricing?.linePricingStatus]}
                    />
                    {winner && (
                        <div className="text-[10px] text-gray-400 mt-0.5 capitalize">via {winner.source}</div>
                    )}
                </td>

                {/* Billable quantity: units billed (and dispatched) for this line */}
                <td className="pl-3 py-3 text-sm text-gray-700">
                    {pricing?.billableQuantity != null ? pricing.billableQuantity : "—"}
                    {pricing?.requiredQuantity != null && Number(pricing.requiredQuantity) !== Number(pricing.billableQuantity) && (
                        <div className="text-[10px] text-gray-400">needs {pricing.requiredQuantity} {pricing.requiredUnit || ""}</div>
                    )}
                </td>

                {/* MRP per unit */}
                <td className="pl-3 py-3 text-sm text-gray-500">
                    {pricing?.unitMrp != null ? `₹${Number(pricing.unitMrp).toFixed(2)}` : "—"}
                </td>

                {/* Line total */}
                <td className="pl-3 py-3 text-sm font-semibold text-gray-800">
                    {pricing?.lineTotal != null ? `₹${pricing.lineTotal}` : "—"}
                </td>

                {/* Total the customer pays for the line, after the cart discount */}
                <td className="pl-3 py-3 text-sm font-semibold text-[#069079]">
                    {pricing?.discountedLineTotal != null ? `₹${pricing.discountedLineTotal}` : "—"}
                    {pricing?.discountedLineTotal != null && pricing?.discountPercent > 0 && (
                        <div className="text-[10px] font-normal text-gray-400">−{pricing.discountPercent}%</div>
                    )}
                </td>

                {/* Sources toggle */}
                <td className="pl-3 py-3 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                        {expanded
                            ? <MdKeyboardArrowDown style={{ fontSize: "16px" }} />
                            : <MdKeyboardArrowRight style={{ fontSize: "16px" }} />
                        }
                        {matchedCount}/{sourcesCount}
                    </div>
                </td>
            </tr>

            {/* Expanded sources sub-row */}
            {expanded && (
                <tr className="bg-gray-50">
                    <td colSpan={9} className="px-6 pb-4 pt-2">
                        <FunnelStrip funnel={line.funnel} />
                        <IdentityComparison funnel={line.funnel} />
                        <SourcesTable sources={pricing?.sources} />
                    </td>
                </tr>
            )}
        </>
    );
}

MedicineTableRow.propTypes = {
    line: PropTypes.shape({
        cartItemId: PropTypes.number,
        itemName:   PropTypes.string.isRequired,
        drugForm:   PropTypes.string,
        doseUnit:   PropTypes.string,
        grounding:  PropTypes.object,
        pricing:    PropTypes.object,
        funnel:     PropTypes.object,
    }).isRequired,
};

function summaryStats(lines) {
    const total  = lines.length;
    const exact  = lines.filter((l) => l.grounding?.decision === "EXACT").length;
    const ready  = lines.filter((l) => l.pricing?.linePricingStatus === "READY").length;
    const review = lines.filter((l) => l.pricing?.needsReview).length;
    return { total, exact, ready, review };
}

const sumOf = (lines, read) => Math.round(lines.reduce((sum, line) => sum + (Number(read(line)) || 0), 0) * 100) / 100;

// Sums over the priced lines only: a failed line has no price and adds nothing.
function lineTotals(lines) {
    const priced = lines.filter((l) => l.pricing?.lineTotal != null);
    return {
        pricedCount: priced.length,
        lineTotal: sumOf(priced, (l) => l.pricing.lineTotal),
        discountedTotal: sumOf(priced, (l) => l.pricing.discountedLineTotal),
    };
}

function StatPill({ label, value, accent }) {
    return (
        <div className="bg-white rounded-2xl shadow-soft-xl px-5 py-4 flex flex-col gap-1 min-w-[120px]">
            <span className="text-xs text-gray-400 font-sans font-medium">{label}</span>
            <span className={`text-xl font-bold font-sans ${accent || "text-gray-800"}`}>{value}</span>
        </div>
    );
}

StatPill.propTypes = {
    label:  PropTypes.string.isRequired,
    value:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    accent: PropTypes.string,
};

StatPill.defaultProps = { accent: "" };

export default function CartReviewDashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [inputValue, setInputValue] = useState(searchParams.get("cartId") || "");
    const cartId = searchParams.get("cartId");
    const view = searchParams.get("view") === "carts" ? "carts" : "summary";

    const { data, isFetching, isError } = useQuery(
        ["cart-review", cartId],
        () => request({ url: `ops/pricing/cart/${cartId}`, method: "GET" }),
        { enabled: !!cartId, refetchOnWindowFocus: false }
    );

    const lines = data?.data?.data?.lines ?? [];
    const journey = data?.data?.data?.journey ?? [];
    const stats = lines.length ? summaryStats(lines) : null;
    const totals = lineTotals(lines);

    const openCart = (id) => {
        const next = new URLSearchParams(searchParams);
        next.set("cartId", String(id));
        setSearchParams(next);
        setInputValue(String(id));
    };

    const closeCart = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("cartId");
        setSearchParams(next);
        setInputValue("");
    };

    const switchView = (nextView) => {
        const next = new URLSearchParams(searchParams);
        next.set("view", nextView);
        next.delete("cartId");
        setSearchParams(next);
        setInputValue("");
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        openCart(trimmed);
    };

    return (
        <div className="py-3 px-5 flex flex-col gap-4 h-full overflow-y-scroll">

            {/* Header */}
            <div className="bg-white rounded-2xl shadow-soft-xl p-5 flex flex-col gap-2">
                <Link to={RouteConstants.CARTS} className="text-xs font-sans text-[#069079] font-semibold underline w-fit">
                    ← Back to carts
                </Link>
                <div className="flex items-center gap-3">
                    <MdInsights className="text-[#069079]" style={{ fontSize: "28px" }} />
                    <div>
                        <h1 className="text-2xl font-semibold font-sans text-gray-800">Cart Review</h1>
                        <p className="text-sm font-sans text-gray-400 mt-0.5">
                            Per-medicine grounding decisions and pricing source breakdown for any cart.
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="flex gap-3 items-center mt-2">
                    <input
                        type="number"
                        min="1"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder="Enter cart ID"
                        className="w-44 rounded-xl border border-gray-200 px-4 py-2 text-sm font-sans text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#069079]"
                    />
                    <button
                        type="submit"
                        disabled={isFetching || !inputValue.trim()}
                        className="px-5 py-2 rounded-xl bg-[#069079] text-white text-sm font-semibold font-sans disabled:opacity-40 hover:bg-[#057a65] transition-colors"
                    >
                        {isFetching ? "Loading…" : "Inspect"}
                    </button>
                    {cartId && <span className="text-xs font-sans text-gray-400">Cart #{cartId}</span>}
                    {cartId && (
                        <button type="button" onClick={closeCart} className="text-xs font-sans text-[#069079] font-semibold underline">
                            ← Back to {view === "carts" ? "carts" : "summary"}
                        </button>
                    )}
                </form>

                {!cartId && (
                    <div className="flex gap-2 mt-3">
                        {[{ key: "summary", label: "Summary" }, { key: "carts", label: "Carts" }].map((option) => (
                            <button
                                key={option.key}
                                type="button"
                                onClick={() => switchView(option.key)}
                                className={`px-4 py-1.5 rounded-lg text-sm font-semibold font-sans ${view === option.key ? "bg-[#069079] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {!cartId && view === "summary" && <FunnelSummary />}
            {!cartId && view === "carts" && <CartList onSelect={openCart} />}

            {isFetching && (
                <div className="flex items-center justify-center py-16">
                    <Loader size="large" text="Loading cart detail…" />
                </div>
            )}

            {!isFetching && isError && (
                <div className="bg-white rounded-2xl shadow-soft-xl px-5 py-8 text-center text-gray-400 font-sans text-sm">
                    Could not load cart {cartId}. Check the ID and try again.
                </div>
            )}

            {!isFetching && !isError && cartId && lines.length === 0 && data && (
                <div className="bg-white rounded-2xl shadow-soft-xl px-5 py-8 text-center text-gray-400 font-sans text-sm">
                    No pricing events recorded for cart #{cartId}. The engine may not have run yet.
                </div>
            )}

            {!isFetching && lines.length > 0 && (
                <>
                    {/* KPI pills */}
                    {stats && (
                        <div className="flex gap-3 flex-wrap">
                            <StatPill label="Medicines" value={stats.total} />
                            <StatPill
                                label="EXACT match"
                                value={`${stats.exact} / ${stats.total}`}
                                accent={stats.exact === stats.total ? "text-green-600" : "text-gray-800"}
                            />
                            <StatPill
                                label="Pricing READY"
                                value={`${stats.ready} / ${stats.total}`}
                                accent={stats.ready === stats.total ? "text-green-600" : "text-gray-800"}
                            />
                            {stats.review > 0 && (
                                <StatPill label="Needs Review" value={stats.review} accent="text-yellow-600" />
                            )}
                        </div>
                    )}

                    <CartJourney journey={journey} />

                    {/* Table */}
                    <div className="bg-white rounded-2xl shadow-soft-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="table-auto w-full font-sans text-sm">
                                <thead>
                                    <tr className="text-left border-b-2 border-gray-100 h-12 text-gray-600">
                                        <th className="pl-3 font-semibold">Medicine</th>
                                        <th className="pl-3 font-semibold">Grounding</th>
                                        <th className="pl-3 font-semibold">Matched ID</th>
                                        <th className="pl-3 font-semibold">Pricing</th>
                                        <th className="pl-3 font-semibold">Billed qty</th>
                                        <th className="pl-3 font-semibold">MRP / unit</th>
                                        <th className="pl-3 font-semibold">Line Total</th>
                                        <th className="pl-3 font-semibold">After discount</th>
                                        <th className="pl-3 font-semibold">Sources</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((line, i) => (
                                        <MedicineTableRow key={line.cartItemId ?? i} line={line} />
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t-2 border-gray-100 h-12 bg-gray-50">
                                        <td colSpan={6} className="pl-3 text-sm font-semibold text-gray-700">
                                            Total <span className="font-normal text-gray-400">({totals.pricedCount} of {lines.length} lines priced)</span>
                                        </td>
                                        <td className="pl-3 text-sm font-bold text-gray-800">₹{totals.lineTotal.toFixed(2)}</td>
                                        <td className="pl-3 text-sm font-bold text-[#069079]">₹{totals.discountedTotal.toFixed(2)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
